import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ThemeToggle from '../components/ThemeToggle';

const Intro = () => {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);

    const features = [
        {
            id: 1,
            title: 'Real-Time Market Data',
            description: 'Access live stock quotes, market trends, and comprehensive financial data. Make informed decisions with up-to-the-second information.',
            icon: '📊',
            gradient: 'from-blue-500 to-cyan-500',
        },
        {
            id: 2,
            title: 'Advanced Analytics',
            description: 'Powerful charting tools, technical indicators, and AI-driven insights to help you spot opportunities before the market does.',
            icon: '📈',
            gradient: 'from-purple-500 to-pink-500',
        },
        {
            id: 3,
            title: 'Portfolio Management',
            description: 'Track your investments, analyze performance, and diversify your portfolio with personalized insights tailored to your goals.',
            icon: '💼',
            gradient: 'from-green-500 to-emerald-500',
        },
        {
            id: 4,
            title: 'Bank-Grade Security',
            description: 'Your investments and data are protected with enterprise-level encryption and multi-factor authentication.',
            icon: '🔒',
            gradient: 'from-orange-500 to-red-500',
        },
    ];

    const testimonials = [
        {
            id: 1,
            name: 'Sarah Chen',
            role: 'Day Trader',
            content: 'Ocktra transformed my trading strategy. The real-time data and analytics are unmatched!',
            rating: 5,
        },
        {
            id: 2,
            name: 'Michael Rodriguez',
            role: 'Investor',
            content: 'Best platform for portfolio management. I\'ve seen a 40% increase in returns since joining.',
            rating: 5,
        },
        {
            id: 3,
            name: 'Emily Watson',
            role: 'Financial Analyst',
            content: 'The security features give me peace of mind. Professional-grade tools at my fingertips.',
            rating: 5,
        },
    ];


    useEffect(() => {
        const testimonialInterval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);

    

        return () => {
            clearInterval(testimonialInterval);
        };
    }, []);

    return (
        <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
            {/* Animated Header with Glassmorphism */}
            <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg shadow-lg transition-all duration-300" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderBottom: '1px solid var(--color-border-light)'
            }}>
                <style>{`.dark-mode header { background-color: rgba(17, 24, 39, 0.8) !important; }`}</style>
                <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                    <div className=" mx-auto" style={{ padding: 'var(--spacing-md)' }}>
                        <div className="flex justify-between items-center">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent animate-pulse">
                                Ocktra
                            </h1>
                            <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                                <ThemeToggle />
                                <Link
                                    to="/login"
                                    className="font-medium transition-all duration-300 hover:scale-105"
                                    style={{ color: 'var(--color-text-secondary)', padding: 'var(--spacing-sm) var(--spacing-md)' }}
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/signup"
                                    className="rounded-full font-semibold text-white transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                                    style={{
                                        background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
                                        boxShadow: 'var(--shadow-lg)',
                                        padding: 'var(--spacing-sm) var(--spacing-lg)'
                                    }}
                                >
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section with Animated Background */}
            <section className="relative overflow-hidden" style={{ paddingTop: 'calc(80px + var(--spacing-3xl))', paddingBottom: 'var(--spacing-3xl)' }}>
                {/* Animated Background Gradient */}
                <div className="absolute inset-0 animate-gradient-xy" style={{
                    background: 'linear-gradient(135deg, #eef2ff 0%, #f3e8ff 50%, #fce7f3 100%)'
                }}></div>
                <style>{`.dark-mode section:nth-of-type(1) > div:first-child { background: linear-gradient(135deg, #111827 0%, #312e81 50%, #581c87 100%) !important; }`}</style>

                {/* Floating Shapes Animation */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 rounded-full filter blur-3xl opacity-30 animate-blob" style={{
                        backgroundColor: '#d8b4fe',
                        mixBlendMode: 'multiply'
                    }}></div>
                    <div className="absolute top-40 right-10 w-72 h-72 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-2000" style={{
                        backgroundColor: '#a5b4fc',
                        mixBlendMode: 'multiply'
                    }}></div>
                    <div className="absolute -bottom-8 left-1/2 w-72 h-72 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-4000" style={{
                        backgroundColor: '#f9a8d4',
                        mixBlendMode: 'multiply'
                    }}></div>
                    <style>{`
                        .dark-mode .animate-blob { mix-blend-mode: soft-light !important; }
                        .dark-mode .animate-blob:nth-child(1) { background-color: #7c3aed !important; }
                        .dark-mode .animate-blob:nth-child(2) { background-color: #4f46e5 !important; }
                        .dark-mode .animate-blob:nth-child(3) { background-color: #a21caf !important; }
                    `}</style>
                </div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="text-center">
                            <h2 className="font-extrabold animate-fade-in-up" style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', lineHeight: '1.1', marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-primary)' }}>
                                Trade Smarter,{' '}
                                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                                    Invest Better
                                </span>
                            </h2>
                            <p className="animate-fade-in-up animation-delay-200" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', marginBottom: 'var(--spacing-xl)', maxWidth: '48rem', marginLeft: 'auto', marginRight: 'auto', color: 'var(--color-text-secondary)' }}>
                                Your complete stock trading platform with real-time data and advanced analytics.
                            </p>


                            <div className="flex flex-col sm:flex-row justify-center items-center animate-fade-in-up animation-delay-600" style={{ gap: 'var(--spacing-md)' }}>
                                <Link
                                    to="/signup"
                                    className="group relative rounded-full font-semibold text-white text-lg transition-all duration-300 transform hover:scale-110 hover:shadow-2xl overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
                                        padding: 'var(--spacing-md) var(--spacing-xl)'
                                    }}
                                >
                                    <span className="relative z-10">Open Free Account</span>
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                                </Link>
                                <Link
                                    to="/login"
                                    className="rounded-full font-semibold border-2 transition-all duration-300 transform hover:scale-110 hover:shadow-xl"
                                    style={{
                                        color: 'var(--color-primary-600)',
                                        borderColor: 'var(--color-primary-600)',
                                        backgroundColor: 'transparent',
                                        padding: 'var(--spacing-md) var(--spacing-xl)'
                                    }}
                                >
                                    Watch Demo
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid with Hover Animations */}
            <section className="relative overflow-hidden" style={{ padding: 'var(--spacing-3xl) 0', marginBottom: 'var(--spacing-3xl)' }}>
                {/* Subtle Background Pattern */}
                <div className="absolute inset-0" style={{
                    backgroundColor: 'var(--color-background)',
                    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.05) 0%, transparent 50%)'
                }}></div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="text-center" style={{ marginBottom: 'var(--spacing-3xl)' }}>
                            <div className="inline-block rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 animate-fade-in-up" style={{
                                padding: 'var(--spacing-xs) var(--spacing-lg)',
                                marginBottom: 'var(--spacing-lg)'
                            }}>
                                <span className="text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    POWERFUL FEATURES
                                </span>
                            </div>
                            <h3 className="font-extrabold animate-fade-in-up animation-delay-200" style={{
                                fontSize: 'clamp(2rem, 5vw, 3rem)',
                                marginBottom: 'var(--spacing-md)',
                                color: 'var(--color-text-primary)'
                            }}>
                                Everything You Need to Trade
                            </h3>
                            <p className="text-xl mx-auto animate-fade-in-up animation-delay-400" style={{ color: 'var(--color-text-secondary)' }}>
                                Professional-grade tools designed for traders of all levels
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: 'var(--spacing-xl)', margin: '0 var(--spacing-lg)' }}>
                            {features.map((feature, index) => (
                                <div
                                    key={feature.id}
                                    className="group relative animate-fade-in-up"
                                    style={{
                                        animationDelay: `${index * 150}ms`
                                    }}
                                >
                                    <div className="relative h-full rounded-2xl transition-all duration-300 hover:shadow-xl" style={{
                                        backgroundColor: 'var(--color-surface)',
                                        border: '1px solid var(--color-border-light)',
                                        padding: 'var(--spacing-xl)'
                                    }}>
                                        {/* Icon Container with Gradient Background */}
                                        <div className={`inline-flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.gradient} transition-transform duration-300 group-hover:scale-110`} style={{
                                            width: '60px',
                                            height: '60px',
                                            marginBottom: 'var(--spacing-lg)'
                                        }}>
                                            <span className="text-3xl filter drop-shadow-lg">{feature.icon}</span>
                                        </div>

                                        {/* Content */}
                                        <h4 className="font-bold transition-colors duration-300" style={{
                                            fontSize: '1.25rem',
                                            marginBottom: 'var(--spacing-sm)',
                                            color: 'var(--color-text-primary)'
                                        }}>
                                            {feature.title}
                                        </h4>
                                        <p className="leading-relaxed" style={{
                                            color: 'var(--color-text-secondary)',
                                            fontSize: '0.9375rem',
                                            lineHeight: '1.6'
                                        }}>
                                            {feature.description}
                                        </p>

                                        {/* Subtle Hover Glow */}
                                        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>

                                        {/* Bottom Accent Line */}
                                        <div className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Vision Analysis Section */}
            <section className="relative overflow-hidden" style={{ padding: 'var(--spacing-2xl) var(--spacing-2xl)', marginBottom: 'var(--spacing-3xl)' }}>
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-violet-900/20"></div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* Content Side */}
                            <div className="order-2 lg:order-1 animate-fade-in-up">
                                <div className="inline-block rounded-full bg-gradient-to-r from-violet-100 to-fuchsia-100 dark:from-violet-900/30 dark:to-fuchsia-900/30" style={{
                                    padding: 'var(--spacing-xs) var(--spacing-lg)',
                                    marginBottom: 'var(--spacing-lg)'
                                }}>
                                    <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                                        🤖 AI-POWERED INSIGHTS
                                    </span>
                                </div>

                                <h3 className="font-extrabold animate-fade-in-up animation-delay-200" style={{
                                    fontSize: 'clamp(2rem, 5vw, 3rem)',
                                    marginBottom: 'var(--spacing-lg)',
                                    color: 'var(--color-text-primary)'
                                }}>
                                    Advanced Vision Models
                                    <span className="block bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                                        Analyze Market Patterns
                                    </span>
                                </h3>

                                <p className="text-lg leading-relaxed animate-fade-in-up animation-delay-300" style={{
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 'var(--spacing-xl)'
                                }}>
                                    Our cutting-edge AI vision models process chart patterns, technical indicators, and market sentiment
                                    in real-time, identifying opportunities that traditional analysis might miss.
                                </p>

                                <div className="space-y-4 animate-fade-in-up animation-delay-400">
                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Pattern Recognition
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                Instantly identify head & shoulders, triangles, flags, and complex chart formations
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Real-Time Processing
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                Analyze thousands of stocks simultaneously with millisecond response times
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Predictive Insights
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                ML-powered forecasting based on historical patterns and market conditions
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Image Side */}
                            <div className="order-1 lg:order-2 animate-fade-in-up animation-delay-200">
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                                    <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 group-hover:from-violet-500/30 group-hover:to-fuchsia-500/30 transition-all duration-500"></div>
                                    <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative overflow-hidden">
                                        {/* Placeholder for chart visualization */}
                                        <div className="absolute inset-0 opacity-30">
                                            <svg viewBox="0 0 800 400" className="w-full h-full">
                                                <defs>
                                                    <linearGradient id="chartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                        <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 0.8 }} />
                                                        <stop offset="100%" style={{ stopColor: '#d946ef', stopOpacity: 0.8 }} />
                                                    </linearGradient>
                                                </defs>
                                                <path d="M 50,300 L 150,250 L 250,280 L 350,180 L 450,200 L 550,120 L 650,150 L 750,80"
                                                    stroke="url(#chartGradient)"
                                                    strokeWidth="3"
                                                    fill="none"
                                                    className="animate-draw-line" />
                                                <circle cx="550" cy="120" r="8" fill="#d946ef" className="animate-pulse" />
                                            </svg>
                                        </div>
                                        <div className="relative z-10 text-center p-8">
                                            <div className="text-6xl mb-4 animate-bounce">🔍</div>
                                            <p className="text-white text-lg font-semibold">AI Vision Analysis</p>
                                            <p className="text-gray-400 text-sm mt-2">Processing market patterns in real-time</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Price Action Analysis Section */}
            <section className="relative overflow-hidden" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-3xl)' }}>
                {/* Background Pattern */}
                <div className="absolute inset-0" style={{
                    backgroundColor: 'var(--color-background)',
                    backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(239, 68, 68, 0.05) 0%, transparent 50%)'
                }}></div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* Image Side */}
                            <div className="animate-fade-in-up">
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                                    <div className="aspect-video bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6 relative">
                                        {/* Grid Lines */}
                                        <div className="absolute inset-6 pointer-events-none">
                                            {[...Array(5)].map((_, i) => (
                                                <div key={i} className="absolute left-0 right-0 border-t border-gray-700/30" style={{ top: `${i * 25}%` }}></div>
                                            ))}
                                            {[...Array(7)].map((_, i) => (
                                                <div key={i} className="absolute top-0 bottom-0 border-l border-gray-700/30" style={{ left: `${i * 16.66}%` }}></div>
                                            ))}
                                        </div>

                                        {/* Candlestick Chart Visualization */}
                                        <div className="h-full flex flex-col relative z-10">
                                            <div className="text-white mb-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-bold text-lg">OCKTRA</span>
                                                    <span className="text-sm text-gray-400">$138.75</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-green-400 font-semibold text-sm">+$15.25</span>
                                                    <span className="text-green-400 font-semibold">+12.5%</span>
                                                </div>
                                            </div>

                                            <div className="flex-1 relative">
                                                {/* Support Line */}
                                                <div className="absolute bottom-[20%] left-0 right-0 border-b-2 border-green-500/60 border-dashed animate-pulse">
                                                    <span className="absolute right-2 -top-6 text-green-400 text-xs font-semibold bg-green-500/20 px-2 py-1 rounded backdrop-blur-sm">
                                                        Support: $124.50
                                                    </span>
                                                </div>

                                                {/* Resistance Line */}
                                                <div className="absolute top-[15%] left-0 right-0 border-b-2 border-red-500/60 border-dashed animate-pulse">
                                                    <span className="absolute right-2 -bottom-6 text-red-400 text-xs font-semibold bg-red-500/20 px-2 py-1 rounded backdrop-blur-sm">
                                                        Resistance: $142.80
                                                    </span>
                                                </div>

                                                {/* Professional Candlesticks */}
                                                <div className="h-full flex items-end px-6 gap-0.5">
                                                    {(() => {
                                                        // Generate 55 realistic candles with varied patterns
                                                        const candles = [];
                                                        let currentPrice = 45;

                                                        for (let i = 0; i < 55; i++) {
                                                            const trend = i < 15 ? 0.3 : i < 30 ? -0.2 : i < 45 ? 0.4 : 0.2;
                                                        
                                                            const isBullish = Math.random() > 0.48 + (trend * 0.5);

                                                            const bodySize = Math.random() * 12 + 3;
                                                            const upperWick = Math.random() * 8 + 2;
                                                            const lowerWick = Math.random() * 8 + 2;

                                                            currentPrice += (isBullish ? 1 : -1) * Math.random() * 2 + trend * 2;
                                                            currentPrice = Math.max(20, Math.min(85, currentPrice));

                                                            const bodyStart = currentPrice;
                                                            const bodyEnd = isBullish ? currentPrice + bodySize : currentPrice - bodySize;
                                                            const wickHigh = Math.max(bodyStart, bodyEnd) + upperWick;
                                                            const wickLow = Math.min(bodyStart, bodyEnd) - lowerWick;

                                                            candles.push({
                                                                bodyStart: `${bodyStart}%`,
                                                                bodyEnd: `${bodyEnd}%`,
                                                                wickHigh: `${wickHigh}%`,
                                                                wickLow: `${wickLow}%`,
                                                                type: isBullish ? 'bullish' : 'bearish',
                                                                volume: Math.random() * 0.5 + 0.4
                                                            });
                                                        }
                                                        return candles;
                                                    })().map((candle, i) => {
                                                        const isBullish = candle.type === 'bullish';
                                                        const bodyTop = isBullish ? candle.bodyEnd : candle.bodyStart;
                                                        const bodyBottom = isBullish ? candle.bodyStart : candle.bodyEnd;
                                                        const bodyHeight = `${Math.abs(parseFloat(candle.bodyEnd) - parseFloat(candle.bodyStart))}%`;

                                                        return (
                                                            <div key={i} className="relative flex-1 max-w-[12px] group/candle" style={{ height: '100%' }}>
                                                                {/* Tooltip on hover */}
                                                                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover/candle:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20 pointer-events-none">
                                                                    <div className="font-semibold">{isBullish ? 'Bullish' : 'Bearish'}</div>
                                                                    <div className="text-gray-300 mt-1">
                                                                        <div>O: ${(128 + i * 2).toFixed(2)}</div>
                                                                        <div>H: ${(132 + i * 2.5).toFixed(2)}</div>
                                                                        <div>L: ${(126 + i * 1.8).toFixed(2)}</div>
                                                                        <div>C: ${(130 + i * 2.2).toFixed(2)}</div>
                                                                    </div>
                                                                </div>

                                                                {/* Upper Wick */}
                                                                <div
                                                                    className="absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 group-hover/candle:brightness-125"
                                                                    style={{
                                                                        width: '2px',
                                                                        backgroundColor: isBullish ? '#22c55e' : '#ef4444',
                                                                        bottom: bodyTop,
                                                                        height: `${parseFloat(candle.wickHigh) - parseFloat(bodyTop)}%`,
                                                                        boxShadow: isBullish
                                                                            ? '0 0 8px rgba(34, 197, 94, 0.4)'
                                                                            : '0 0 8px rgba(239, 68, 68, 0.4)'
                                                                    }}
                                                                ></div>

                                                                {/* Candle Body */}
                                                                <div
                                                                    className="absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 group-hover/candle:scale-110 group-hover/candle:brightness-125 cursor-pointer"
                                                                    style={{
                                                                        width: '100%',
                                                                        maxWidth: '24px',
                                                                        bottom: bodyBottom,
                                                                        height: bodyHeight,
                                                                        backgroundColor: isBullish ? '#22c55e' : '#ef4444',
                                                                        border: `1.5px solid ${isBullish ? '#16a34a' : '#dc2626'}`,
                                                                        borderRadius: '2px',
                                                                        boxShadow: isBullish
                                                                            ? '0 2px 8px rgba(34, 197, 94, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                                                                            : '0 2px 8px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                                                                        background: isBullish
                                                                            ? 'linear-gradient(to right, #22c55e, #16a34a)'
                                                                            : 'linear-gradient(to right, #ef4444, #dc2626)',
                                                                        animation: `fadeInCandle 0.5s ease-out ${i * 0.1}s backwards`
                                                                    }}
                                                                ></div>

                                                                {/* Lower Wick */}
                                                                <div
                                                                    className="absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 group-hover/candle:brightness-125"
                                                                    style={{
                                                                        width: '2px',
                                                                        backgroundColor: isBullish ? '#22c55e' : '#ef4444',
                                                                        bottom: candle.wickLow,
                                                                        height: `${parseFloat(bodyBottom) - parseFloat(candle.wickLow)}%`,
                                                                        boxShadow: isBullish
                                                                            ? '0 0 8px rgba(34, 197, 94, 0.4)'
                                                                            : '0 0 8px rgba(239, 68, 68, 0.4)'
                                                                    }}
                                                                ></div>

                                                                {/* Volume Bar */}
                                                                <div
                                                                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 transition-all duration-300"
                                                                    style={{
                                                                        width: '100%',
                                                                        maxWidth: '20px',
                                                                        height: `${candle.volume * 15}%`,
                                                                        backgroundColor: isBullish ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                                                        borderTop: `1px solid ${isBullish ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
                                                                    }}
                                                                ></div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="mt-4 flex justify-between text-xs text-gray-400 font-medium">
                                                <span>9:30 AM</span>
                                                <span>11:00 AM</span>
                                                <span>12:30 PM</span>
                                                <span>2:00 PM</span>
                                                <span>4:00 PM</span>
                                            </div>

                                            {/* Live indicator */}
                                            <div className="absolute top-1 right-1 flex items-center gap-2 bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-500/50"></div>
                                                <span className="text-xs text-gray-300 font-medium">LIVE</span>
                                            </div>
                                        </div>

                                        {/* Animation keyframes */}
                                        <style>{`
                                            @keyframes fadeInCandle {
                                                from {
                                                    opacity: 0;
                                                    transform: translateY(10px);
                                                }
                                                to {
                                                    opacity: 1;
                                                    transform: translateY(0);
                                                }
                                            }
                                        `}</style>
                                    </div>
                                </div>
                            </div>

                            {/* Content Side */}
                            <div className="animate-fade-in-up animation-delay-200">
                                <div className="inline-block rounded-full bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30" style={{
                                    padding: 'var(--spacing-xs) var(--spacing-lg)',
                                    marginBottom: 'var(--spacing-lg)'
                                }}>
                                    <span className="text-sm font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                        📊 TECHNICAL ANALYSIS
                                    </span>
                                </div>

                                <h3 className="font-extrabold animate-fade-in-up animation-delay-300" style={{
                                    fontSize: 'clamp(2rem, 5vw, 3rem)',
                                    marginBottom: 'var(--spacing-lg)',
                                    color: 'var(--color-text-primary)'
                                }}>
                                    Advanced Price Action
                                    <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                        & Candlestick Analysis
                                    </span>
                                </h3>

                                <p className="text-lg leading-relaxed animate-fade-in-up animation-delay-400" style={{
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 'var(--spacing-xl)'
                                }}>
                                    Master the markets with sophisticated candlestick analysis tools. Identify critical support and
                                    resistance levels, recognize powerful patterns, and make data-driven trading decisions.
                                </p>

                                <div className="space-y-4 animate-fade-in-up animation-delay-500">
                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Dynamic Support & Resistance
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                Auto-detect key price levels with adaptive algorithms that adjust to market volatility
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Multi-Timeframe Analysis
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                Analyze candlestick patterns across 1-minute to monthly charts simultaneously
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-xs)' }}>
                                                Pattern Recognition
                                            </h4>
                                            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                                Instantly identify doji, hammer, engulfing, and 50+ candlestick formations
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonial Carousel */}
            <section className="relative overflow-hidden" style={{ padding: 'var(--spacing-3xl) 0', marginBottom: 'var(--spacing-3xl)' }}>
                {/* Background with subtle pattern */}
                <div className="absolute inset-0" style={{
                    backgroundColor: 'var(--color-background)',
                    backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(168, 85, 247, 0.08) 0%, transparent 50%)'
                }}></div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        {/* Section Header */}
                        <div className="text-center" style={{ marginBottom: 'var(--spacing-3xl)' }}>
                            <div className="inline-block rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 animate-fade-in-up" style={{
                                padding: 'var(--spacing-xs) var(--spacing-lg)',
                                marginBottom: 'var(--spacing-lg)'
                            }}>
                                <span className="text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    TESTIMONIALS
                                </span>
                            </div>
                            <h3 className="font-extrabold animate-fade-in-up animation-delay-200" style={{
                                fontSize: 'clamp(2rem, 5vw, 3rem)',
                                color: 'var(--color-text-primary)'
                            }}>
                                Trusted by Traders Worldwide
                            </h3>
                        </div>

                        {/* Testimonial Cards Carousel */}
                        <div className="relative flex items-center justify-center" style={{ height: '360px' }}>
                            {testimonials.map((testimonial, index) => (
                                <div
                                    key={testimonial.id}
                                    className={`absolute transition-all duration-700 ${index === currentTestimonial
                                        ? 'opacity-100 scale-100 translate-x-0'
                                        : index < currentTestimonial
                                            ? 'opacity-0 scale-75 -translate-x-full'
                                            : 'opacity-0 scale-75 translate-x-full'
                                        }`}
                                >
                                    <div className="rounded-3xl max-w-2xl shadow-xl transition-shadow duration-300" style={{
                                        backgroundColor: 'var(--color-surface)',
                                        border: '2px solid var(--color-border-light)',
                                        padding: 'var(--spacing-2xl)'
                                    }}>
                                        {/* Rating Stars */}
                                        <div className="flex justify-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <span key={i} className="text-yellow-500 text-3xl">★</span>
                                            ))}
                                        </div>

                                        {/* Quote */}
                                        <p className="text-xl italic text-center" style={{
                                            marginBottom: 'var(--spacing-xl)',
                                            color: 'var(--color-text-primary)',
                                            lineHeight: '1.6'
                                        }}>
                                            "{testimonial.content}"
                                        </p>

                                        {/* Author Info */}
                                        <div className="text-center">
                                            <div className="font-bold text-lg bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                                {testimonial.name}
                                            </div>
                                            <div className="text-sm" style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
                                                {testimonial.role}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Carousel Indicators */}
                        <div className="flex justify-center" style={{ gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-xl)' }}>
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTestimonial(index)}
                                    className={`rounded-full transition-all duration-300 ${index === currentTestimonial
                                        ? 'w-8'
                                        : 'hover:opacity-75'
                                        }`}
                                    style={{
                                        width: index === currentTestimonial ? '32px' : '12px',
                                        height: '12px',
                                        backgroundColor: index === currentTestimonial
                                            ? 'var(--color-primary-600)'
                                            : 'var(--color-border-light)'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section with Animation */}
            <section className="relative overflow-hidden" style={{ padding: 'var(--spacing-3xl) 0' }}>
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600"></div>

                {/* Animated Floating Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob"></div>
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
                </div>

                <div className="relative w-full z-10">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="text-center">
                            {/* Badge */}
                            <div className="inline-flex items-center backdrop-blur-sm bg-white/20 rounded-full animate-fade-in-up" style={{
                                padding: 'var(--spacing-sm) var(--spacing-lg)',
                                marginBottom: 'var(--spacing-xl)',
                                border: '1px solid rgba(255,255,255,0.3)'
                            }}>
                                <span className="text-white font-semibold">🚀 Join 500,000+ Active Traders</span>
                            </div>

                            <h2 className="font-extrabold text-white animate-fade-in-up animation-delay-200" style={{
                                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                                marginBottom: 'var(--spacing-xl)',
                                lineHeight: '1.1',
                                textShadow: '0 4px 20px rgba(0,0,0,0.2)'
                            }}>
                                Ready to Start Your<br />Trading Journey?
                            </h2>

                            <p className="text-xl text-white/90  mx-auto animate-fade-in-up animation-delay-400" style={{
                                marginBottom: 'var(--spacing-2xl)',
                                lineHeight: '1.6'
                            }}>
                                Get started in minutes with zero commission trades. No hidden fees, no minimum deposit required.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row justify-center items-center animate-fade-in-up animation-delay-600" style={{ gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-2xl)' }}>
                                <Link
                                    to="/signup"
                                    className="group relative rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 overflow-hidden shadow-2xl"
                                    style={{
                                        backgroundColor: 'white',
                                        color: 'var(--color-primary-600)',
                                        padding: 'var(--spacing-md) var(--spacing-2xl)',
                                        minWidth: '200px'
                                    }}
                                >
                                    <span className="relative z-10">Start Trading Free</span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 text-white font-bold transition-opacity duration-300 z-20">
                                        Let's Go! →
                                    </span>
                                </Link>
                                <Link
                                    to="/login"
                                    className="rounded-full font-bold text-lg border-2 border-white text-white transition-all duration-300 transform hover:scale-105 hover:bg-white hover:text-purple-600"
                                    style={{
                                        padding: 'var(--spacing-md) var(--spacing-2xl)',
                                        minWidth: '200px',
                                        backgroundColor: 'transparent'
                                    }}
                                >
                                    Watch Demo
                                </Link>
                            </div>

                            {/* Trust Indicators */}
                            <div className="flex flex-wrap justify-center items-center animate-fade-in-up animation-delay-800" style={{ gap: 'var(--spacing-lg)' }}>
                                <div className="flex items-center text-white/80" style={{ gap: 'var(--spacing-xs)' }}>
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">No Credit Card Required</span>
                                </div>
                                <div className="flex items-center text-white/80" style={{ gap: 'var(--spacing-xs)' }}>
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">Setup in 2 Minutes</span>
                                </div>
                                <div className="flex items-center text-white/80" style={{ gap: 'var(--spacing-xs)' }}>
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">24/7 Support</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{ backgroundColor: 'var(--color-gray-900)', padding: 'var(--spacing-3xl)' }}>
                <div className="w-full">
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
                        <div className="grid md:grid-cols-4" style={{ gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-xl)' }}>
                            <div>
                                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent" style={{ marginBottom: 'var(--spacing-md)' }}>
                                    Ocktra
                                </h3>
                                <p className="text-gray-400">
                                    Professional stock trading platform for modern investors.
                                </p>
                            </div>
                            <div>
                                <h4 className="text-white font-semibold" style={{ marginBottom: 'var(--spacing-md)' }}>Product</h4>
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Security</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-white font-semibold" style={{ marginBottom: 'var(--spacing-md)' }}>Company</h4>
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-white font-semibold" style={{ marginBottom: 'var(--spacing-md)' }}>Legal</h4>
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a></li>
                                    <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Compliance</a></li>
                                </ul>
                            </div>
                        </div>
                        <div className="text-center text-gray-400" style={{ borderTop: '1px solid var(--color-gray-800)', paddingTop: 'var(--spacing-xl)' }}>
                            <p>&copy; 2025 Ocktra. All rights reserved. Trading involves risk.</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Intro;
